import React, { useState, useEffect } from "react";
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletReadyState } from '@solana/wallet-adapter-base';

export function WalletModal({ onClose, onWalletSelected }) {
  const [view, setView] = useState("list");
  const [search, setSearch] = useState("");

  const { wallets, select } = useWallet();

  // Origin & DApp URL
  const [dappUrl, setDappUrl] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDappUrl(encodeURIComponent(window.location.origin));
    }
  }, []);

  // Wallet Configs ordered: Phantom - Solflare - Backpack - Trust Wallet
  const walletConfigs = [
    {
      match: (n) => n === 'Phantom',
      label: 'Phantom',
      icon: '/images/wallets/phantom.svg',
      deepLink: `https://phantom.app/ul/v1/browse/${dappUrl}?ref=${dappUrl}`,
    },
    {
      match: (n) => n === 'Solflare',
      label: 'Solflare',
      icon: '/images/wallets/solflare.svg',
      deepLink: `https://solflare.com/ul/v1/browse/${dappUrl}?ref=${dappUrl}`,
    },
    {
      match: (n) => n.toLowerCase().includes('backpack'),
      label: 'Backpack',
      icon: '/images/wallets/backpack.svg',
      deepLink: `https://backpack.app/ul/v1/browse/${dappUrl}?ref=${dappUrl}`,
    },
    {
      match: (n) => n.toLowerCase().includes('trust'),
      label: 'Trust Wallet',
      icon: '/images/wallets/trustwallet.svg',
      deepLink: `https://link.trustwallet.com/open_url?url=${dappUrl}`,
    },
  ];

  const mappedWallets = walletConfigs.map((cfg) => {
    const w = wallets.find((w) => cfg.match(w.adapter.name));
    return w ? { adapter: w.adapter, readyState: w.readyState, ...cfg } : null;
  });

  const orderedWallets = mappedWallets.filter(
    (x) => x !== null
  );

  const isDesktop = () => {
    if (typeof navigator === 'undefined') return true;
    const uaData = navigator.userAgentData;
    if (uaData && typeof uaData.mobile === 'boolean') return !uaData.mobile;
    const ua = navigator.userAgent;
    const isMobileUA = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua);
    return !isMobileUA;
  };

  const handleWalletClick = async (w) => {
    onClose();

    // 1. ADAPTER PİPELİNE & ORTAM ALGILAMA
    // Eğer cüzdan zaten kuruluysa (Extension kurulu, ya da Mobil In-App Tarayıcı içerisindeyiz),
    // ortam fark etmeksizin (Mobil veya Masaüstü) direkt bağlantı akışını başlat.
    const isInstalled = w.readyState === WalletReadyState.Installed || w.readyState === WalletReadyState.Loadable;
    
    // In-App Browser doğrulaması (Cüzdanın kendi tarayıcısında mıyız?)
    const hasInjectedObject = 
      (w.label === "Phantom" && window.solana?.isPhantom) ||
      (w.label === "Solflare" && (window.solflare?.isSolflare || window.solana?.isSolflare)) ||
      (w.label === "Backpack" && (window.backpack?.isBackpack || window.solana?.isBackpack)) ||
      (w.label === "Trust Wallet" && (window.trustWallet?.isTrust || window.solana?.isTrust)) ||
      (typeof window !== 'undefined' && !!window.solana);

    if (isInstalled || hasInjectedObject) {
      try {
        console.log(`[Adapter Pipeline] ${w.label} kurulu veya injekte durumda. Direkt cüzdan bağlantısı başlatılıyor...`);
        await select(w.adapter.name);
        if (onWalletSelected) onWalletSelected(w.label);
      } catch (err) {
        console.error(`[Adapter Pipeline] Cüzdan seçim hatası:`, err);
      }
      return;
    }

    // 2. CÜZDAN KURULU DEĞİLSE (DEEP LINK / İNDİRME AKIŞI)
    const desktop = isDesktop();
    const currentUrl = encodeURIComponent(window.location.href);
    
    // Dinamik ve güncel deep link oluşturma (DApp'in tam aktif sayfa adresiyle)
    let finalDeepLink = "";
    if (w.label === "Phantom") {
      finalDeepLink = `https://phantom.app/ul/v1/browse/${currentUrl}?ref=${currentUrl}`;
    } else if (w.label === "Solflare") {
      finalDeepLink = `https://solflare.com/ul/v1/browse/${currentUrl}?ref=${currentUrl}`;
    } else if (w.label === "Backpack") {
      finalDeepLink = `https://backpack.app/ul/v1/browse/${currentUrl}?ref=${currentUrl}`;
    } else if (w.label === "Trust Wallet") {
      finalDeepLink = `https://link.trustwallet.com/open_url?url=${currentUrl}`;
    } else {
      finalDeepLink = w.deepLink;
    }

    if (desktop) {
      console.log(`[Adapter Pipeline] Masaüstü ortamı. Cüzdan indirme sayfasına yönlendiriliyor...`);
      window.open(finalDeepLink, '_blank');
      return;
    }

    // Mobildeyiz ve cüzdan enjekte edilmemiş -> Deep Link ile ilgili uygulamayı aç
    console.log(`[Adapter Pipeline] Mobil tarayıcı. Deep link tetikleniyor:`, finalDeepLink);
    window.location.href = finalDeepLink;
    if (onWalletSelected) onWalletSelected(w.label);
  };

  const filteredWallets = orderedWallets.filter(w => w.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }} />
      <div
        className="relative rounded-[32px] shadow-2xl w-full max-w-[390px] overflow-hidden"
        style={{ background: "#121212", color: "#ffffff", zIndex: 1, border: "1px solid rgba(255,255,255,0.08)" }}
        onClick={e => e.stopPropagation()}
      >
        {view === "list" ? (
          <>
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
              <button onClick={() => setView("help")} className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </button>
              <h2 className="text-lg font-bold text-white tracking-wide">Cüzdan Bağla</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-4 py-4 space-y-2">
              {filteredWallets.map(w => (
                <button
                  key={w.label}
                  className="flex items-center w-full px-4 py-3.5 rounded-2xl transition-all hover:bg-white/[0.08] gap-3 bg-white/[0.04] border border-white/5"
                  onClick={() => handleWalletClick(w)}
                >
                  <div className="shrink-0">
                    <img src={w.icon} alt={w.label} className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center" style={{ objectFit: 'contain' }} />
                  </div>
                  <span className="flex-1 text-[16px] font-bold text-white/90 text-left">{w.label}</span>
                  {w.label === "Phantom" ? (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded" style={{ background: "rgba(134,239,172,0.15)", color: "#22c55e" }}>
                      ÖNERİLEN
                    </span>
                  ) : w.label === "Solflare" ? (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded" style={{ background: "rgba(134,239,172,0.15)", color: "#22c55e" }}>
                      POPÜLER
                    </span>
                  ) : w.label === "Trust Wallet" ? (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded" style={{ background: "rgba(127, 29, 29, 0.15)", color: "#b91c1c" }}>
                      SADECE ANDROID
                    </span>
                  ) : null}
                </button>
              ))}
            </div>

            <div className="px-4 pb-4">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Cüzdan Ara"
                  className="w-full pl-9 pr-10 py-2.5 rounded-xl text-sm bg-white/[0.05] text-white placeholder-gray-500 outline-none border border-white/5 focus:border-white/10 transition-colors"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">{filteredWallets.length}</span>
              </div>
            </div>

            <div className="text-center py-3 text-xs text-gray-500 border-t border-white/5">
              UX by <span className="font-semibold">MaykutoGPT</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
              <button onClick={() => setView("list")} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-white/10">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
              </button>
              <h2 className="text-base font-semibold text-white">Kripto Cüzdan Nedir?</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-white/10">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-6 space-y-4">
              {[
                {
                  title: "Tüm web3 için tek oturum",
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                    </svg>
                  ),
                  desc: "Tek bir cüzdan ile tüm web3 uygulamalarında oturum açabilirsiniz."
                },
                {
                  title: "Dijital varlıklarınızın evi",
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 2 7 12 12 22 7 12 2" />
                      <polyline points="2 12 12 17 22 12" />
                      <polyline points="2 17 12 22 22 17" />
                    </svg>
                  ),
                  desc: "Cüzdanlar, dijital varlıklarınızı saklamak ve transfer etmek için kullanılır."
                },
                {
                  title: "Yeni internete açılan kapınız",
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                    </svg>
                  ),
                  desc: "Merkeziyetsiz uygulamalara bağlanın ve yeni dünyayı keşfedin."
                },
              ].map(item => (
                <div key={item.title} className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.05)" }}>
                    {item.icon}
                  </div>
                  <div className="pt-0.5">
                    <p className="font-bold text-[14px] text-white leading-tight">{item.title}</p>
                    <p className="text-[12px] text-gray-400 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
              <button
                onClick={() => window.open('https://phantom.app/download', '_blank')}
                className="w-full py-3 rounded-xl font-semibold text-sm mt-2 transition-all hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #7f1d1d, #b91c1c)", color: "#ffffff", boxShadow: "0 4px 16px rgba(127, 29, 29, 0.4)" }}
              >
                Cüzdan Edin
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
